// IMPORTANT NOTE: Please don't import other selectors from this file.
// Use secondOrder and *ThirdOrder for your selector if it has other
// dependencies.

import { createSelector } from 'reselect'
import { groupBy, mapValues } from 'lodash'

import { fullFileStateSelector } from './fullFileFirstOrder'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').Attribute} Attribute
 * @typedef {import('../index').Attributes} Attributes
 * @typedef {import('../index').AttributeType} AttributeType
 */

/**
 * @type {function(PlottrState): Attributes}
 */
export const attributesSelector = createSelector(fullFileStateSelector, (state) => {
  return state.attributes ?? { characters: [] }
})

/**
 * @type {function(PlottrState): Array<Attribute>}
 */
export const characterAttributesForBookSelector = createSelector(
  attributesSelector,
  (attributes) => {
    return attributes?.characters ?? []
  }
)

/**
 * @type {function(PlottrState, number): number}
 */
const attributeIdSelector = (_state, attributeId) => {
  return attributeId
}
/**
 * @type {function(PlottrState, number): (Attribute | Null)}
 */
export const singleCharacterAttributeSelector = createSelector(
  characterAttributesForBookSelector,
  attributeIdSelector,
  (attributes, attributeId) => {
    return (
      attributes.find(({ id }) => {
        return id === attributeId
      }) ?? null
    )
  }
)

/**
 * @type {function(PlottrState): Array<Attribute>}
 */
export const allCharacterAttributesSelector = createSelector(attributesSelector, (attributes) => {
  return attributes.characters
})

/**
 * @type {function(PlottrState): Array<Attribute>}
 */
export const allNonBaseCharacterAttributesSelector = createSelector(
  attributesSelector,
  (attributes) => {
    return attributes.characters.filter((attribute) => {
      return attribute.type !== 'base-attribute'
    })
  }
)

/**
 * @type {function(PlottrState): Array<Attribute>}
 */
export const allBaseCharacterAttributesSelector = createSelector(
  attributesSelector,
  (attributes) => {
    return (attributes?.characters ?? []).filter((attribute) => {
      return attribute.type == 'base-attribute'
    })
  }
)

/**
 * @type {function(PlottrState): Record<String, Attribute>}
 */
export const characterAttributsForBookByIdSelector = createSelector(
  characterAttributesForBookSelector,
  (attributeDescriptors) => {
    return mapValues(groupBy(attributeDescriptors, 'id'), '0')
  }
)

/**
 * @type {function(PlottrState): (number | Null)}
 */
export const characterPositionAttributeIdSelector = createSelector(
  characterAttributesForBookSelector,
  (availableAttributes) => {
    const position = Object.values(availableAttributes).find(({ name }) => name === 'position')
    return position?.id ?? null
  }
)

/**
 * @type {function(PlottrState): (number | Null)}
 */
export const characterCategoryAttributeIdSelector = createSelector(
  characterAttributesForBookSelector,
  (availableAttributes) => {
    const category = Object.values(availableAttributes).find(({ name }) => name === 'category')
    return category?.id ?? null
  }
)
